import { Box, Button, CircularProgress, List, Modal, Typography, useMediaQuery, useTheme } from '@mui/material';
import SidebarItem from 'components/Layouts/components/SidebarItem';
import { configure } from 'routes/config';
import { Outlet, useParams } from 'react-router-dom';
import IdentityIcon from 'assets/icons/IdentityIcon';
import KnowledgeIcon from 'assets/icons/KnowledgeIcon';
import ActionsIcon from 'assets/icons/ActionsIcon';
import DeployPhoneIcon from 'assets/icons/DeployPhoneIcon';
import DeployWebIcon from 'assets/icons/DeployWebIcon';
import PersonalizedLinkIcon from 'assets/icons/PersonalizedLinkIcon';
import { useGetAgent } from 'apis/business/agent/hook';
import { useEffect, useState } from 'react';
import { useAgent } from 'providers/context/AgentProvider';
import SupportAgentIcon from 'assets/icons/SupportAgentIcon';

export default function Configure() {
  const theme = useTheme();
  const { agentId } = useParams();
  const { data: agent } = useGetAgent();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [progress, setProgress] = useState(0);
  const [label, setLabel] = useState('Gathering Information');
  const [isModalOpen, setIsModalOpen] = useState(localStorage.getItem('configuringAgent') === 'true');
  const [isModalOpen2, setIsModalOpen2] = useState(false);
  const { selectedAgent } = useAgent();

  useEffect(() => {
    if (localStorage.getItem('configuringAgent') === 'true') {
      const labels = ['Gathering Information', 'Analyzing Content', 'Optimizing Insights'];
      const duration = 10000;
      const interval = duration / 100;

      const timer = setInterval(() => {
        setProgress((prevProgress) => {
          const nextProgress = prevProgress + 1;
          if (nextProgress <= 33) setLabel(labels[0]);
          else if (nextProgress <= 66) setLabel(labels[1]);
          else setLabel(labels[2]);

          return nextProgress >= 100 ? 100 : nextProgress;
        });
      }, interval);

      if (progress === 100 && localStorage.getItem('configuringAgent') === 'true') {
        localStorage.removeItem('configuringAgent');
        setIsModalOpen(false);
        setIsModalOpen2(true);
        clearInterval(timer);
      }

      return () => clearInterval(timer);
    } else {
      if (localStorage.getItem('configuringAgent') === 'withoutWebUrl') {
        localStorage.removeItem('configuringAgent');
        setIsModalOpen2(true);
      }
    }
  }, [progress]);

  const handleTalk = async () => {
    const botRoot = document.getElementById('kickCall-chatbot-root');
    if (botRoot) botRoot.remove();
    (window as any).VoiceBot?.open({ agentToken: selectedAgent?.agent_token, isForPreview: true, isTest: true });
    setIsModalOpen2(false);
  };

  return (
    <>
      <Box display='flex' gap={4} minHeight='100vh'>
        {!isMediumScreen ? (
          <List
            sx={{
              minWidth: 262,
              maxWidth: 262,
              height: 'fit-content',
              background: theme.palette.background.paper,
              borderRadius: 2,
              position: 'sticky',
              top: '200px'
            }}
          >
            <SidebarItem
              to={configure.identity}
              icon={<IdentityIcon />}
              label='Identity'
              isCompleted={agent?.progress_status?.identity}
            />

            <SidebarItem
              to={configure.knowledge}
              icon={<KnowledgeIcon />}
              label='Knowledge'
              disabled={!Boolean(agentId)}
              isCompleted={agent?.progress_status?.knowledge}
            />
            <SidebarItem
              to={configure.action}
              icon={<ActionsIcon />}
              label='Actions'
              disabled={!Boolean(agentId)}
              isCompleted={agent?.progress_status?.actions}
            />
            <SidebarItem
              to={configure.deployWeb}
              icon={<DeployWebIcon />}
              label='Deploy Web'
              disabled={!Boolean(agentId)}
              isCompleted={agent?.progress_status?.deploy_web}
            />
            <SidebarItem
              to={configure.deployPhone}
              icon={<DeployPhoneIcon />}
              label='Deploy Phone'
              isCompleted={agent?.progress_status?.deploy_phone}
            />
            <SidebarItem
              to={configure.personalizeLink}
              icon={<PersonalizedLinkIcon />}
              label='Personalized Link'
              // isCompleted={agent?.progress_status?.personalized_link}
            />
          </List>
        ) : (
          <List
            className='mobile-sidebar'
            sx={{
              display: 'flex',
              height: 'fit-content',
              background: theme.palette.background.paper,
              borderRadius: '24px',
              position: 'fixed',
              bottom: '20px',
              overflow: 'auto',
              zIndex: '1000',
              backgroundColor: '#EAE6FD',
              width: '-webkit-fill-available',
              marginRight: '24px'
            }}
          >
            <SidebarItem type='mobile' to={configure.identity} icon={<IdentityIcon />} label='Identity' />

            <SidebarItem
              type='mobile'
              to={configure.knowledge}
              icon={<KnowledgeIcon />}
              label='Knowledge'
              disabled={!Boolean(agentId)}
            />
            <SidebarItem
              type='mobile'
              to={configure.action}
              icon={<ActionsIcon />}
              label='Actions'
              disabled={!Boolean(agentId)}
            />
            <SidebarItem
              type='mobile'
              to={configure.deployPhone}
              icon={<DeployPhoneIcon />}
              label='Deploy Phone'
              disabled={!Boolean(agentId)}
            />
            <SidebarItem
              type='mobile'
              to={configure.deployWeb}
              icon={<DeployWebIcon />}
              label='Deploy Web'
              disabled={!Boolean(agentId)}
            />
            <SidebarItem
              type='mobile'
              to={configure.personalizeLink}
              icon={<PersonalizedLinkIcon />}
              label='Personalized Link'
              disabled={!Boolean(agentId)}
            />
          </List>
        )}
        <Box width='100%' paddingBottom='100px'>
          <Outlet />
        </Box>
      </Box>
      <Modal open={isModalOpen} aria-labelledby='modal-modal-title' aria-describedby='modal-modal-description'>
        <Box sx={style}>
          <CircularProgressWithLabel value={progress} label={label} />
          <Typography fontSize={14} fontWeight={500} textAlign='center' marginTop={2}>
            The agent scans your website for key data and structures.
          </Typography>
        </Box>
      </Modal>
      <Modal
        open={isModalOpen2}
        onClose={() => setIsModalOpen2(false)}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box sx={style}>
          <SupportAgentIcon height={44} width={44} color={theme.palette.primary.main} />
          <Typography variant='h6' fontWeight={500} textAlign='center' marginTop={1}>
            Congratulation, Your Agent Is Ready to Go!
          </Typography>
          <Typography color='secondary' fontSize={14}>
            AI agent ready! Explore and adjust features as needed.
          </Typography>
          <Box display='flex' justifyContent='end' gap={2} mt={3}>
            <Button
              color='secondary'
              variant='outlined'
              sx={{ border: '1px solid #F3F3F3' }}
              onClick={() => setIsModalOpen2(false)}
            >
              Customize Settings
            </Button>
            <Button color='primary' onClick={() => handleTalk()}>
              Test Agent
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  border: '1px solid #F3F3F3',
  boxShadow: 24,
  p: 3,
  borderRadius: '8px',
  textAlign: 'center',
  width: {
    xs: '90%',
    sm: 468
  }
};

function CircularProgressWithLabel(props: { value: number; label: string }) {
  return (
    <Box position='relative' display='inline-flex'>
      <CircularProgress variant='determinate' value={props.value} size={160} thickness={4} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography
          fontSize={14}
          color='secondary'
          textAlign='center'
          maxWidth='70%'
          sx={{
            wordBreak: 'break-word'
          }}
        >
          {props.label}
        </Typography>
      </Box>
    </Box>
  );
}
